import { Close } from "@mui/icons-material";
import { Box, ButtonBase as Button, InputAdornment, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { visuallyHidden } from "@mui/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

import { isPublicPage } from "@/utils/visualBuilder";

import FormAutoComplete from "../../../components/UI/FormAutoComplete";
import { SEARCH_OPTION_SECTION, SEARCH_OPTION_TYPE, TAB_STATUSES } from "../../../constants";
import { useUrlParams } from "../../../hooks";
import { Search } from "../../../icons";
import { updateVisualBuilderData } from "../../../store/actions";
import { searchOptionsSelector, visualBuilderDataSelector } from "../../../store/selectors";

const useStyles = makeStyles({
  search: {
    width: "100%",
  },
  inputBase: {
    display: "flex",
    height: "65px",
    flexWrap: "wrap",
    backgroundColor: "rgba(255,255,255,.2)",
    marginTop: "1px",
    borderRadius: "0",
  },
  input: {
    color: "#fff",
    width: "0",
    minWidth: "calc(100% - 56px)",
    paddingLeft: "9px",
  },
  searchIcon: {
    fontSize: "1rem",
  },
  chip: {
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.87)",
    },
  },
  chipIcon: {
    padding: "5px",
    margin: "4px 4px 4px 4px",
  },
  chipLabel: {
    padding: "0 8px 0 8px",
  },
  textField: {
    flexDirection: "column",
  },
  searchBoolean: {
    padding: "0",
  },
  searchBooleanIcon: {
    fontSize: "1.5rem",
  },
  listItemButtonRoot: {
    padding: "6px 16px",
  },
});

export default function SearchField({ visualBuilderRef }) {
  const classes = useStyles();
  const params = useUrlParams();
  const dispatch = useDispatch();

  const isSearchedByOnChange = useRef(false);

  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));
  const { searchOptions: options, searches = [] } = useSelector(
    searchOptionsSelector(params, SEARCH_OPTION_SECTION.VISUAL_BUILDER),
  );

  const { tabStatus } = params;
  const { search = {}, hiddenTags = {}, locked } = visualBuilderData;
  const { tagIds = [], contentTypeIds = [], searchText = "" } = search;

  const [searchValue, setSearchValue] = useState(searchText);
  const [selectedOption, setSelectedOption] = useState(null);

  const searchOptions = useMemo(() => {
    const visibleOptions = options.filter((option) => {
      if (tabStatus === TAB_STATUSES.CUSTOMIZE) {
        return !hiddenTags[`media/${option.value}`];
      }

      if (isPublicPage(tabStatus) || tabStatus === TAB_STATUSES.SHARE) {
        return !hiddenTags[option.value];
      }

      return true;
    });

    return [...new Set([...searches.filter((value) => options.includes(value)), ...visibleOptions])];
  }, [options.length, searches[0], hiddenTags]);

  useEffect(() => {
    if (!tagIds.length && !contentTypeIds.length && !searchText) {
      setSearchValue("");
      setSelectedOption(null);
    }
  }, [tagIds.length, contentTypeIds.length, searchText]);

  useEffect(() => {
    if (!searchText && !searchValue && tagIds.length) {
      setSelectedOption(searchOptions.find((option) => option.value === tagIds[0]));
      setSearchValue(searchOptions.find((option) => option.value === tagIds[0])?.name || "");
    }
  }, [searchOptions.length, searchText, tagIds.length]);

  const updateSearchOnSelected = useCallback(
    async (selectedTag) => {
      if (!selectedOption && !selectedTag) {
        return;
      }

      if (selectedTag?.name) {
        setSearchValue(selectedTag.name);
        setSelectedOption(selectedTag);
      }

      if (!selectedTag)
        return dispatch(updateVisualBuilderData(params, { search: {}, shouldToggleTagItem: true }, {}, true));

      const payload = {
        search: {
          ...search,
          searchAndOr: true,
          searchText: selectedTag?.name ? "" : searchValue,
          tagIds: [],
          contentTypeIds: [],
        },
        typeFilterId: null,
        tagFilterId: null,
      };

      if (selectedTag?.type === SEARCH_OPTION_TYPE.TYPE) {
        payload.search.contentTypeIds.push(selectedTag?.value);
      } else if (selectedTag?.type === SEARCH_OPTION_TYPE.TAG) {
        payload.search.tagIds.push(selectedTag?.value);
      }

      isSearchedByOnChange.current = true;

      dispatch(updateVisualBuilderData(params, payload, { shouldToggleTagItem: true }, true));

      visualBuilderRef.current?.focus();
    },
    [selectedOption, search, params],
  );

  const updateSearchOnType = useDebouncedCallback(async (text) => {
    if (isSearchedByOnChange.current) {
      isSearchedByOnChange.current = false;
      return;
    }

    if (!text) return dispatch(updateVisualBuilderData(params, { search: {} }, { shouldToggleTagItem: true }, true));

    const searchText = text.toLowerCase();

    const payload = {
      search: {
        ...search,
        searchAndOr: true,
        searchText,
        tagIds: [],
        contentTypeIds: [],
      },
      typeFilterId: null,
      tagFilterId: null,
    };

    dispatch(updateVisualBuilderData(params, payload, { shouldToggleTagItem: true }, true));

    visualBuilderRef.current?.focus();
  }, 800);

  const handleSearchReset = () => {
    setSearchValue("");
    setSelectedOption(null);
    dispatch(updateVisualBuilderData(params, { search: {} }, { shouldToggleTagItem: true }, true));
  };

  return (
    <div className={classes.search}>
      <FormAutoComplete
        multiple={false}
        value={selectedOption}
        testId="search-container"
        onChange={(e, value) => updateSearchOnSelected(value)}
        onInputChange={(e, value, reason) => {
          if (reason !== "input") {
            return;
          }

          setSearchValue(value);
          setSelectedOption(null);
          updateSearchOnType(value);
        }}
        inputValue={searchValue}
        options={searchOptions}
        isInCustomizeMode={tabStatus === TAB_STATUSES.CUSTOMIZE}
        locked={locked}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            variant="filled"
            placeholder="search"
            fullWidth
            classes={{ root: classes.textField }}
            inputProps={{
              ...inputParams.inputProps,
              "data-testid": "search-field",
            }}
            InputProps={{
              ...inputParams.InputProps,
              className: null,
              classes: { root: classes.inputBase, input: classes.input },
              disableUnderline: true,
              endAdornment: (
                <InputAdornment
                  aria-label="search"
                  style={{
                    backgroundColor: "transparent",
                    marginRight: "10px",
                  }}
                  position="end"
                >
                  {searchValue ? (
                    <Button onClick={handleSearchReset}>
                      <Box component="span" sx={visuallyHidden}>
                        Clear search
                      </Box>
                      <Close
                        fontSize="small"
                        sx={{
                          color: "#ffffff80",
                        }}
                      />
                    </Button>
                  ) : (
                    <Search classes={{ root: classes.searchIcon }} />
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </div>
  );
}
